import {
  Command,
  Proposition,
  Random,
  ValueProposition,
  deduplicate,
  isJsonEqual,
} from "~/utils";
import { ActionSlotDef } from "../actionSlot";
import { cardsDefRepository } from "../card/cardsDefRepository";
import {
  AgentIcon,
  FactionType,
  Meaple,
  ResourceType,
  TurnPhase,
  UnitType,
  agentIconToFactionType,
  factionTypeToAgentIcon,
} from "../constant";
import { AgentEffectArgs } from "./AgentEffectArgs";
import { AgentEffectResult } from "./AgentEffectChoices";
import { AgentEffectFunction } from "./AgentEffectFunction";
import { sellSpiceAmounts } from "./sellSpiceAmounts";
import { gameEventBuilder } from "../GameEvent";

const flagEffect = (): AgentEffectFunction =>
  new AgentEffectFunction(() => {
    return true;
  });

const AgentEffectFunctionsRaw = {
  draw(): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      player.draw(1);
      return true;
    });
  },
  drawIntrigue(): AgentEffectFunction {
    return new AgentEffectFunction(({ player, game }: AgentEffectArgs) => {
      game.intrigueDeck.moveCount(player.intrigueCards, 1);
      return true;
    });
  },
  stealIntrigue(): AgentEffectFunction {
    return new AgentEffectFunction(({ player, game }: AgentEffectArgs) => {
      const otherPlayers = game.getOpponents(player.id);
      otherPlayers.forEach((otherPlayer) => {
        const { intrigueCards } = otherPlayer;
        if (intrigueCards.length >= 4) {
          const stolenCard = Random.pickOne(intrigueCards.pile);
          intrigueCards.move(player.intrigueCards, stolenCard);
          game.infoEvents.push(
            gameEventBuilder("stealIntrigueCard")({
              fromPlayerId: otherPlayer.id,
              toPlayerId: player.id,
            })
          );
        }
      });
      return true;
    });
  },
  trashMe(): AgentEffectFunction {
    return new AgentEffectFunction(
      ({ player, card, techTile, game }: AgentEffectArgs) => {
        if (!card && !techTile) {
          throw new Error("card or techTile expected");
        }
        const { trashedTechTiles } = game;
        const { playZone, trashZone, techTiles, revealZone } = player;
        if (techTile) {
          techTiles.move(trashedTechTiles, techTile);
        } else if (card) {
          const containingZone = playZone.findById(card.id)
            ? playZone
            : revealZone;
          containingZone.move(trashZone, card);
        }
        return true;
      }
    );
  },
  trashMeOptional(): AgentEffectFunction {
    return new AgentEffectFunction(({ player, card }: AgentEffectArgs) => {
      if (!card) {
        throw new Error("card expected");
      }
      return [
        {
          playerId: player.id,
          optional: true,
          type: "trashMe",
          possibleChoices: [{ cardId: card.id, willTrash: true }],
        },
      ];
    });
  },
  trashCard({
    onlyInPlay = false,
  }: {
    onlyInPlay?: boolean;
  }): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const { discard, hand, playZone, revealZone } = player;
      const zones = [hand, revealZone];
      if (!onlyInPlay) {
        zones.push(discard);
      }
      const possiblePlayedCards = playZone.pile.filter((card) =>
        card
          .getDef(cardsDefRepository)
          .playEffects.every(
            (effect) => !AgentEffectDescription.is(effect, "trashMe")
          )
      );
      const possibleCards = zones
        .flatMap((zone) => zone.pile)
        .concat(possiblePlayedCards);
      return [
        {
          playerId: player.id,
          type: "trashCard",
          possibleChoices: possibleCards.map((card) => ({ cardId: card.id })),
          optional: true,
        },
      ];
    });
  },
  discardCard(): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const { hand } = player;
      const possibleCards = hand.pile;
      if (!possibleCards.length) {
        return false;
      }
      return [
        {
          playerId: player.id,
          type: "discardCard",
          possibleChoices: possibleCards.map((card) => ({ cardId: card.id })),
          optional: true,
        },
      ];
    });
  },
  influencePoints(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      player.bonusInfluencePoints += amount;
      return true;
    });
  },
  combatPoint(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      player.bonusCombatPoints += amount;
      return true;
    });
  },
  victoryPoint(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      player.victoryPoints += amount;
      return true;
    });
  },
  spice(amount: number): AgentEffectFunction {
    return AgentEffectFunction.addResource(amount, ResourceType.Spice);
  },
  water(amount: number): AgentEffectFunction {
    return AgentEffectFunction.addResource(amount, ResourceType.Water);
  },
  solaris(amount: number): AgentEffectFunction {
    return new AgentEffectFunction((args: AgentEffectArgs) => {
      const { player, game } = args;
      const { turnPhase } = game;
      const {
        playerLeader: { globalEffect },
      } = player;
      const finalAmount =
        AgentEffectDescription.is(globalEffect, "solarisBlessing") &&
        [TurnPhase.Play, TurnPhase.Reveal, TurnPhase.Shop].includes(turnPhase)
          ? amount + 1
          : amount;
      return AgentEffectFunction.addResource(
        finalAmount,
        ResourceType.Solaris
      ).apply(args);
    });
  },
  garnison(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      player.moveToGarnison(Math.min(amount, player.orbitTroops.length));
      return true;
    });
  },
  hasSiege() {
    return new AgentEffectFunction(
      ({ player }: AgentEffectArgs) => player.hasSiege
    );
  },
  takeSiege(): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      player.hasSiege = true;
      return true;
    });
  },
  buyWorker(): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      if (player.hasExtraMeaple) {
        return false;
      }
      player.workerPile.push(Meaple.new(`${player.id}-3`, player.id));
      player.hasExtraMeaple = true;
      return true;
    });
  },
  buySpaceWarp(): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const card = cardsDefRepository.fromName("Foldspace");
      player.discard.push(card);
      return true;
    });
  },

  mentat(): AgentEffectFunction {
    return new AgentEffectFunction(({ game, player }: AgentEffectArgs) => {
      if (game.mentatOnSlotOpt) {
        player.workerPile.push(game.mentatOnSlotOpt);
        game.mentatOnSlotOpt = undefined;
      }
      return true;
    });
  },
  takeMentatFromAnywhere() {
    return new AgentEffectFunction(({ game, player }: AgentEffectArgs) => {
      const mentat = game.retrieveMentatFromAnywhere();
      player.workerPile.push(mentat);
      game.skipCleanMentat = true;
      return true;
    });
  },
  factionUp(faction: FactionType): AgentEffectFunction {
    return new AgentEffectFunction(({ game, player }: AgentEffectArgs) => {
      game.factionLevelChange(faction, player, 1);
      return true;
    });
  },
  emperorUp(): AgentEffectFunction {
    return this.factionUp(FactionType.Emperor);
  },
  guildUp(): AgentEffectFunction {
    return this.factionUp(FactionType.Guild);
  },
  beneGesseritUp(): AgentEffectFunction {
    return this.factionUp(FactionType.BeneGesserit);
  },
  fremenUp(): AgentEffectFunction {
    return this.factionUp(FactionType.Fremen);
  },
  fremenBond() {
    return new AgentEffectFunction(({ player, card }) => {
      if (!card) {
        throw new Error("fremenBond cannot be used without a card");
      }
      const { playZone, revealZone } = player;
      const foundCard = [...playZone.pile, ...revealZone.pile].find(
        (otherCard) =>
          card.id !== otherCard.id &&
          cardsDefRepository
            .getDef(otherCard)
            .factions.includes(FactionType.Fremen)
      );
      return Boolean(foundCard);
    });
  },
  hasPlayedBeneGesseritCard() {
    return new AgentEffectFunction(({ player, card }: AgentEffectArgs) => {
      const otherBenegesseritCard = player.playZone.pile.find(
        (otherCard) =>
          otherCard !== card &&
          cardsDefRepository
            .getDef(otherCard)
            .factions.includes(FactionType.BeneGesserit)
      );
      return Boolean(otherBenegesseritCard);
    });
  },
  chooseFactionChange(amount: number): AgentEffectFunction {
    return this.multipleFactionChange({
      factionCount: 1,
      factionUpdate: amount,
    });
  },
  multipleFactionChange({
    factionCount,
    factionUpdate,
    factions = Object.values(FactionType),
  }: {
    factionUpdate: number;
    factionCount: number;
    factions?: FactionType[];
  }): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const factionsWithInfluence = player.factionAffinities.flatMap(
        (affinity) => (affinity.level > 0 ? [affinity.faction] : [])
      );
      const factionChoices =
        factionUpdate < 0 ? factionsWithInfluence : factions;
      if (!factionChoices.length) {
        return false;
      }
      return [
        {
          playerId: player.id,
          type: "chooseFactionChange",
          optional: factionUpdate < 0,
          possibleChoices: factionChoices.map((faction) => ({
            faction,
            amount: factionUpdate,
          })),
          choiceCount: factionCount,
        },
      ];
    });
  },
  chooseFactionUpWhereBehind() {
    return new AgentEffectFunction(({ player, game }: AgentEffectArgs) => {
      const opponents = game.getOpponents(player.id);

      const behindFactions = Object.values(FactionType).filter((faction) =>
        opponents.some(
          (opponent) =>
            opponent.getFactionAffinity(faction).level >
            player.getFactionAffinity(faction).level
        )
      );
      return this.multipleFactionChange({
        factionCount: 1,
        factionUpdate: 1,
        factions: behindFactions,
      }).apply({ player, game });
    });
  },
  payResource({
    amount,
    type,
  }: {
    amount: number;
    type: ResourceType;
  }): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const cost = {
        [type]: amount,
      };
      const canPay = player.resources.canPay(cost);
      if (!canPay) {
        return false;
      }
      return [
        {
          playerId: player.id,
          optional: true,
          possibleChoices: [{ willPay: true, cost }],
          type: "payResource",
        },
      ];
    });
  },
  hasAnyAlliance() {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      return Boolean(
        Object.values(FactionType).find(
          (faction) => player.getFactionAffinity(faction).hasPoint
        )
      );
    });
  },
  hasAlliance(faction: FactionType) {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      return player.getFactionAffinity(faction).hasPoint;
    });
  },
  hasInfluenceLevel2(faction: FactionType) {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      return player.getFactionAffinity(faction).level >= 2;
    });
  },
  drawBenegesseritInDiscsard() {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      return [
        {
          playerId: player.id,
          type: "drawBenegesseritCardFromDiscard",
          optional: true,
          possibleChoices: player.discard.pile
            .filter((card) =>
              cardsDefRepository
                .getDef(card)
                .factions.includes(FactionType.BeneGesserit)
            )
            .map((card) => ({ cardId: card.id })),
        },
      ];
    });
  },
  canDeployToConflict({
    amount,
    type,
    nextUnitType,
  }: {
    amount: number;
    type: UnitType;
    nextUnitType?: UnitType;
  }): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const garnison = player.getGarnison(type);
      const maxDeploy = Math.min(garnison.length, amount);
      if (!maxDeploy) {
        return false;
      }
      return [
        {
          playerId: player.id,
          possibleChoices: [...new Array(maxDeploy + 1)].map((_, index) => ({
            troopCount: index,
            type,
            nextUnitType,
            maxUnitCount: amount,
          })),
          type: "canDeployToConflict",
        },
      ];
    });
  },
  canRetreat(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const { combatTroops } = player;
      const maxRetreat = Math.min(combatTroops.length, amount);
      if (!maxRetreat) {
        return false;
      }
      return [
        {
          playerId: player.id,
          possibleChoices: [...new Array(maxRetreat + 1)].map((_, index) => ({
            troopCount: index,
          })),
          type: "canRetreat",
        },
      ];
    });
  },
  shouldRetreat(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const { combatTroops } = player;
      if (combatTroops.length < amount) {
        return false;
      }
      return [
        {
          playerId: player.id,
          possibleChoices: [
            {
              troopCount: amount,
            },
          ],
          type: "canRetreat",
          optional: true,
        },
      ];
    });
  },
  opponentsDiscard(amount: number): AgentEffectFunction {
    return new AgentEffectFunction(({ player, game }: AgentEffectArgs) => {
      const otherPlayers = game.getOpponents(player.id);
      return otherPlayers.flatMap((otherPlayer) => {
        const { hand } = otherPlayer;
        const possibleChoices = hand.pile.map(({ id }) => ({
          cardId: id,
          playerId: otherPlayer.id,
        }));
        if (!possibleChoices.length) return [];
        return [
          {
            playerId: otherPlayer.id,
            type: "opponentsDiscard",
            possibleChoices,
            choiceCount: Math.min(amount, possibleChoices.length),
          },
        ];
      });
    });
  },
  opponentsLooseCombatTroop(amount: number) {
    return new AgentEffectFunction(({ game, player }: AgentEffectArgs) => {
      const otherPlayers = game.getOpponents(player.id);
      otherPlayers.forEach((player) => {
        const { combatTroops, orbitTroops } = player;
        if (!combatTroops.isEmpty()) {
          combatTroops.moveCount(orbitTroops, amount);
        }
      });
      return true;
    });
  },
  looseTroop(amount: number) {
    return new AgentEffectFunction(({ player }: AgentEffectArgs) => {
      const { garnisonTroops, orbitTroops } = player;
      if (garnisonTroops.length < amount) return false;
      garnisonTroops.moveCount(orbitTroops, amount);
      return true;
    });
  },
  opponentsLooseGarnisonTroop(amount: number) {
    return new AgentEffectFunction(({ game, player }: AgentEffectArgs) => {
      const otherPlayers = game.getOpponents(player.id);
      otherPlayers.forEach((player) => {
        const { garnisonTroops, orbitTroops } = player;
        if (!garnisonTroops.isEmpty()) {
          garnisonTroops.moveCount(orbitTroops, amount);
        }
      });
      return true;
    });
  },
  shuffleDiscardInDeck() {
    return new AgentEffectFunction(({ player }) => {
      const { discard, deck } = player;
      discard.moveAll(deck);
      deck.pile = Random.shuffle(deck.pile);
      return true;
    });
  },
  canPassTurn() {
    return new AgentEffectFunction(({ player }) => {
      return [
        {
          playerId: player.id,
          possibleChoices: [true, false].map((value) => ({ passTurn: value })),
          type: "canPassTurn",
        },
      ];
    });
  },
  recallAgent() {
    return new AgentEffectFunction(({ player, game }) => {
      const actionSlotsWithAgent = game.actionSlots.filter((slot) =>
        slot.meaples.pile.some((meaple) => meaple.owner === player.id)
      );
      if (!actionSlotsWithAgent.length) return false;
      return [
        {
          optional: true,
          playerId: player.id,
          possibleChoices: actionSlotsWithAgent.map((slot) => ({
            slotId: slot.id,
          })),
          type: "recallAgent",
        },
      ];
    });
  },
  chooseLooseConflictTroops(amount: number) {
    return new AgentEffectFunction(({ player }) => {
      const { combatTroops } = player;
      if (combatTroops.length >= amount) {
        return [
          {
            playerId: player.id,
            possibleChoices: [{ looseConflictTroops: amount }],
            optional: true,
            type: "chooseLooseConflictTroops",
          },
        ];
      } else {
        return false;
      }
    });
  },
  hasNCardTSMF(min: number) {
    return new AgentEffectFunction(({ player }) => {
      return player.TSMFCardCount >= min;
    });
  },
  hasMoreTSMFThanOpponents() {
    return new AgentEffectFunction(({ game, player }) => {
      const SMFs = player.TSMFCardCount;
      const opponents = game.getOpponents(player.id);
      const hasMore = opponents.every(
        (opponent) => opponent.TSMFCardCount < SMFs
      );
      return hasMore;
    });
  },
  hasFactionsLevel({
    factionCount = 4,
    minLevel,
    maxLevel,
  }: {
    factionCount?: number;
    minLevel?: number;
    maxLevel?: number;
  }) {
    return new AgentEffectFunction(({ player }) => {
      let count = 0;
      if (minLevel !== undefined) {
        count = Object.values(FactionType).filter(
          (faction) => player.getFactionAffinity(faction).level >= minLevel
        ).length;
      } else if (maxLevel !== undefined) {
        count = Object.values(FactionType).filter(
          (faction) => player.getFactionAffinity(faction).level <= maxLevel
        ).length;
      }
      return count >= factionCount;
    });
  },
  replaceOpponentConflictTroop() {
    return new AgentEffectFunction(({ game, player }) => {
      const { orbitTroops } = player;
      const otherPlayersWithTroops = game
        .getOpponents(player.id)
        .filter((opponent) => opponent.combatTroops.length);
      if (!otherPlayersWithTroops.length || !orbitTroops.length) return true;
      return [
        {
          playerId: player.id,
          possibleChoices: otherPlayersWithTroops.map((player) => ({
            opponentId: player.id,
          })),
          type: "replaceOpponentConflictTroop",
        },
      ];
    });
  },
  ignoreAgentIcons() {
    return new AgentEffectFunction(({ player }) => {
      player.ignoreAgentIcons = true;
      return true;
    });
  },
  ignoreOccupiedAgentSlots() {
    return new AgentEffectFunction(({ player }) => {
      player.ignoreOccupiedAgentSlots = true;
      return true;
    });
  },
  chooseGoToBattle({ type }: { type: UnitType }) {
    return new AgentEffectFunction((args) => {
      const { player } = args;
      const { startOfTurnGarnison } = player;
      const garnison = player.getGarnison(type);
      if (!garnison.length) {
        return true;
      }
      const bonusDeployedDreadnought = Math.max(
        0,
        startOfTurnGarnison.dreadnought -
          player.getGarnison("dreadnought").length
      );
      const gainedCount = Math.max(
        0,
        garnison.length - startOfTurnGarnison[type]
      );
      const maxDeploy = gainedCount + 2 - bonusDeployedDreadnought;
      return this.canDeployToConflict({ amount: maxDeploy, type }).apply(args);
    });
  },
  canDeployToConflictUnit(amount: number) {
    return new AgentEffectFunction((args) => {
      const { player } = args;
      if (!player.getGarnison("dreadnought").length) {
        return this.canDeployToConflict({ amount, type: "troop" }).apply(args);
      }
      return this.canDeployToConflict({
        amount,
        type: "dreadnought",
        nextUnitType: "troop",
      }).apply(args);
    });
  },
  endPlayerTurn() {
    return new AgentEffectFunction(({ game }) => {
      game.endPlayerTurn();
      return true;
    });
  },
  endRound() {
    return new AgentEffectFunction(({ game }) => {
      game.endRound();
      return true;
    });
  },
  resolveWinnerDreadnought() {
    return new AgentEffectFunction(({ game }) => {
      game.resolveWinnersDreadnought();
      return true;
    });
  },
  sellSpice() {
    return new AgentEffectFunction(({ player }) => {
      return [
        {
          playerId: player.id,
          possibleChoices: sellSpiceAmounts.flatMap(({ spice }) =>
            player.resources.canPay({ [ResourceType.Spice]: spice })
              ? [
                  {
                    spiceAmount: spice,
                  },
                ]
              : []
          ),
          optional: true,
          type: "sellSpice",
        },
      ];
    });
  },
  influenceByPlayedFactionCard({
    amount,
    faction,
  }: {
    amount: number;
    faction: FactionType;
  }) {
    return new AgentEffectFunction(({ player }) => {
      const { playZone, revealZone } = player;
      const factionCards = [...playZone.pile, ...revealZone.pile].filter(
        (card) => cardsDefRepository.getDef(card).factions.includes(faction)
      );
      const influenceBonus = amount * factionCards.length;
      player.bonusInfluencePoints += influenceBonus;
      return true;
    });
  },
  doubleBaseSpice() {
    return new AgentEffectFunction(() => {
      return true;
    });
  },
  toNextFightingPlayer() {
    return new AgentEffectFunction(({ game }) => {
      game.toNextFightingPlayer();
      return true;
    });
  },
  gainControl(actionSlot: ActionSlotDef) {
    return new AgentEffectFunction(({ player, game }) => {
      const updatedSlot = game.actionSlots.find(
        ({ id }) => id === actionSlot.id
      );
      if (!updatedSlot) {
        throw new Error("unexpected: could not find updated action slot");
      }
      updatedSlot.controlFlag = player.id;
      return true;
    });
  },
  opponentDiscardOrLooseTroop() {
    return new AgentEffectFunction(({ player, game }) => {
      const opponents = game.getOpponents(player.id);
      return opponents.flatMap((opponent) => {
        const { combatTroops, hand } = opponent;
        const hasTroopInConflict = combatTroops.length;
        const possibleChoices = [
          ...(hasTroopInConflict ? [{ looseTroop: 1 }] : []),
          ...hand.pile.map((card) => ({ cardId: card.id })),
        ];
        if (possibleChoices.length) {
          return [
            {
              playerId: opponent.id,
              possibleChoices,
              type: "opponentDiscardOrLooseTroop",
            },
          ];
        } else {
          return [];
        }
      });
    });
  },
  reserveShopCardWithDiscount(discount: number) {
    return new AgentEffectFunction(({ player, game }) => {
      const { shop } = game;
      return [
        {
          playerId: player.id,
          possibleChoices: shop.pile.map((card) => ({
            cardId: card.id,
            discount,
          })),
          type: "reserveShopCardWithDiscount",
        },
      ];
    });
  },
  signet: flagEffect,
  playAnywhere: flagEffect,
  TSMFDiscount(discount: number) {
    return new AgentEffectFunction(({ player }) => {
      player.TSMFDiscount += discount;
      return true;
    });
  },
  drawOrTrashTopCard() {
    return new AgentEffectFunction(({ player }) => {
      const cardId = player.lookAtTop().id;
      return [
        {
          playerId: player.id,
          type: "drawOrTrash",
          possibleChoices: [
            {
              cardId,
              type: "drawOrTrash",
              action: "draw",
            },
            {
              cardId,
              type: "drawOrTrash",
              action: "trash",
            },
          ],
        },
      ];
    });
  },
  canBuyOnDeck() {
    return new AgentEffectFunction(({ player }) => {
      player.canBuyOnDeck = true;
      return true;
    });
  },
  buyOnDeck(cardId: string) {
    return new AgentEffectFunction(({ player }) => {
      return [
        {
          playerId: player.id,
          type: "buyOnDeck",
          optional: true,
          possibleChoices: [
            {
              cardId,
              buyOnDeck: true,
            },
          ],
        },
      ];
    });
  },
  freeCard({ cardMaxCost, onTop }: { cardMaxCost: number; onTop?: boolean }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { ArrakisLiaisonCard, shop } = game;
      const cards = shop.pile.filter(
        (card) => cardsDefRepository.getDef(card).cost <= cardMaxCost
      );
      return [
        {
          playerId: player.id,
          type: "freeCard",
          possibleChoices: [...cards, ArrakisLiaisonCard].map((card) => ({
            cardId: card.id,
            onTop,
          })),
          optional: true,
        },
      ];
    });
  },
  landsraadPopularity: flagEffect,
  eyesEverywhere: flagEffect,
  spiceAdict() {
    return new AgentEffectFunction(({ player }) => {
      const cost = {
        [ResourceType.Spice]: 1,
      };
      if (player.resources.canPay(cost)) {
        player.resources.pay(cost);
        player.draw(1);
      }
      return true;
    });
  },
  prescience: flagEffect,
  siegeConnections() {
    return new AgentEffectFunction((props) => {
      return this.chooseFactionChange(1).apply(props);
    });
  },
  ruthlessNegotiator() {
    return new AgentEffectFunction(({ player }) => {
      player.draw(1);
      return true;
    });
  },
  masterstroke() {
    return new AgentEffectFunction((props) => {
      return this.multipleFactionChange({
        factionCount: 2,
        factionUpdate: 1,
      }).apply(props);
    });
  },
  checkEndBuy() {
    return new AgentEffectFunction(({ game }) => {
      game.checkEndBuy();
      return true;
    });
  },
  checkEndReveal() {
    return new AgentEffectFunction(({ game }) => {
      game.checkEndReveal();
      return true;
    });
  },
  blockActionSlot() {
    return new AgentEffectFunction(({ game, player }) => {
      const possibleChoices = game.actionSlots.map((slot) => ({
        slotId: slot.id,
      }));
      return [
        {
          playerId: player.id,
          type: "blockActionSlot",
          possibleChoices,
        },
      ];
    });
  },
  freighter() {
    return new AgentEffectFunction(({ game, player }) => {
      const { freighterLevel } = player;
      if (freighterLevel >= 3) {
        return this.recallFreighter().apply({ game, player });
      }
      if (freighterLevel <= 0) {
        return this.freighterUp().apply({ game, player });
      }
      return [
        {
          playerId: player.id,
          type: "freighterChoice",
          possibleChoices: [{ freighter: "up" }, { freighter: "recall" }],
        },
      ];
    });
  },
  freighterUp() {
    return new AgentEffectFunction(({ player }) => {
      player.freighterLevel = Math.min(player.freighterLevel + 1, 3);
      return true;
    });
  },
  opponentsSolaris({
    personalAmount,
    opponentsAmount,
  }: {
    personalAmount: number;
    opponentsAmount: number;
  }) {
    return new AgentEffectFunction((args) => {
      const { player, game } = args;
      const opponents = game.getOpponents(player.id);
      opponents.forEach((opponent) => {
        opponent.resources.add({
          [ResourceType.Solaris]: opponentsAmount,
        });
      });
      this.solaris(personalAmount).apply(args);
      return true;
    });
  },
  recallFreighter() {
    return new AgentEffectFunction(({ game, player }) => {
      const { executor } = game;
      const { freighterLevel } = player;
      const {
        or,
        spice,
        garnison,
        chooseFactionChange,
        acquireTechTile,
        opponentsSolaris,
        shippingTrackTroop,
      } = AgentEffectCommands;
      const orderedEffects = [
        or([
          opponentsSolaris({ personalAmount: 5, opponentsAmount: 1 }),
          spice(2),
        ]),
        and([garnison(2), chooseFactionChange(1)]),
        acquireTechTile({ discount: 2 }),
      ];
      const appliedEffects: AgentEffectDescription[] = orderedEffects.slice(
        0,
        freighterLevel
      );
      if (
        game.hasTechTileEffect(player, "shippingTrackTroop", TurnPhase.Play) &&
        freighterLevel >= 2
      ) {
        appliedEffects.push(shippingTrackTroop);
      }
      executor.addAllInReverse(appliedEffects, {
        playerId: player.id,
      });
      player.freighterLevel = 0;
      return true;
    });
  },
  acquireTechTile({
    discount = 0,
    mayBuyWithSolaris = false,
  }: {
    discount?: number;
    mayBuyWithSolaris?: boolean;
  }) {
    return new AgentEffectFunction(({ game, player }) => {
      const { techTiles } = game;
      const { techNegociators, resources } = player;
      const buyableTechtiles = techTiles.flatMap((techPile) => {
        const techTile = techPile.last();
        if (!techTile) return [];
        const { cost } = techTile;
        const finalCost = Math.max(0, cost - discount - techNegociators.length);
        const canPay =
          resources.canPay({ [ResourceType.Spice]: finalCost }) ||
          (mayBuyWithSolaris &&
            resources.canPay({ [ResourceType.Solaris]: finalCost }));
        return canPay ? [techTile] : [];
      });
      if (!buyableTechtiles.length) return false;
      return [
        {
          playerId: player.id,
          type: "acquireTechTile",
          optional: true,
          possibleChoices: buyableTechtiles.map((techTile) => ({
            techTileId: techTile.id,
            discount,
            mayBuyWithSolaris,
          })),
        },
      ];
    });
  },
  choosePaymentForTechTile({ cost }: { cost: number }) {
    return new AgentEffectFunction(({ player }) => {
      return [
        {
          playerId: player.id,
          type: "choosePaymentForTechTile",
          possibleChoices: [
            {
              cost,
              resourceType: ResourceType.Solaris,
            },
            {
              cost,
              resourceType: ResourceType.Spice,
            },
          ],
        },
      ];
    });
  },
  dreadnought() {
    return new AgentEffectFunction(({ player }) => {
      const { garnisonDreadnought, orbitDreadnought } = player;
      orbitDreadnought.moveCount(
        garnisonDreadnought,
        Math.min(1, orbitDreadnought.length)
      );
      return true;
    });
  },
  discardIntrigueCard() {
    return new AgentEffectFunction(({ player }) => {
      const { intrigueCards } = player;
      if (!intrigueCards.length) {
        return false;
      }
      return [
        {
          playerId: player.id,
          type: "discardIntrigueCard",
          possibleChoices: intrigueCards.pile.map((card) => ({
            intrigueCardId: card.id,
          })),
          optional: true,
        },
      ];
    });
  },
  techNegociator() {
    return new AgentEffectFunction(({ player }) => {
      const { techNegociators, orbitTroops } = player;
      const troop = orbitTroops.last();
      if (!troop) return false;
      orbitTroops.move(techNegociators, troop);
      return true;
    });
  },
  combatPointPerDreadnought(amount: number) {
    return new AgentEffectFunction(({ player }) => {
      const { combatDreadnought } = player;
      player.bonusCombatPoints += amount * combatDreadnought.length;
      return true;
    });
  },
  heighlinerDiscount(amount: number) {
    return new AgentEffectFunction(() => {
      return true;
    });
  },
  hasAgentOnFactionSlot(faction: FactionType) {
    return new AgentEffectFunction(({ game, player }) => {
      const { actionSlots } = game;
      const searchedAgentIcon = factionTypeToAgentIcon(faction);
      return actionSlots.some(
        (slot) =>
          slot.agentIcon === searchedAgentIcon &&
          slot.meaples.pile.some((meaple) => meaple.owner === player.id)
      );
    });
  },
  mayPlayAgain() {
    return new AgentEffectFunction(({ player }) => {
      return [
        {
          playerId: player.id,
          type: "playAgain",
          possibleChoices: [{ playAgain: true }],
          optional: true,
        },
      ];
    });
  },
  combatPointForReavealedCard(bonusPointPerCard: number) {
    return new AgentEffectFunction(({ player, card }) => {
      const { revealZone } = player;
      // TODO handle more complicated effects
      const cardsWithSwords = revealZone.pile.filter(
        (cardIte) =>
          (!card || cardIte.id != card.id) &&
          cardIte
            .getDef(cardsDefRepository)
            .revealEffects.some((effect) =>
              AgentEffectDescription.is(effect, "combatPoint")
            )
      );
      player.bonusCombatPoints += cardsWithSwords.length * bonusPointPerCard;
      return true;
    });
  },
  hasResource({ amount, type }: { amount: number; type: ResourceType }) {
    return new AgentEffectFunction(({ player }) => {
      return player.getResource(type) >= amount;
    });
  },
  hasMinInfluencePoints(minimum: number) {
    return new AgentEffectFunction(({ player }) => {
      const { influencePoints } = player;
      return influencePoints >= minimum;
    });
  },
  spyStallites() {
    return new AgentEffectFunction(({ player }) => {
      const count = Object.values(FactionType).filter(
        (faction) => player.getFactionAffinity(faction).level <= 1
      ).length;
      player.victoryPoints += count;
      return true;
    });
  },
  recycleIntrigueCards() {
    return new AgentEffectFunction(({ player }) => {
      const { intrigueCards } = player;
      if (!intrigueCards.length) {
        return false;
      }
      player.checkpointIntrigueCardCount = intrigueCards.length;
      return [
        {
          playerId: player.id,
          type: "discardIntrigueCard",
          possibleChoices: intrigueCards.pile.map((card) => ({
            intrigueCardId: card.id,
          })),
          choiceCount: 9999,
          optional: true,
        },
      ];
    });
  },
  drawIntrigueCardsToCheckpoint() {
    return new AgentEffectFunction((args) => {
      const { player } = args;
      const { intrigueCards, checkpointIntrigueCardCount } = player;
      if (intrigueCards.length >= checkpointIntrigueCardCount) {
        return false;
      }
      const cardsToDraw = checkpointIntrigueCardCount - intrigueCards.length;
      [...new Array(cardsToDraw)].forEach(() =>
        this.drawIntrigue().apply(args)
      );
      return true;
    });
  },
  winTiebreakers: flagEffect,
  hasDeployedDreadnaught() {
    return new AgentEffectFunction(({ player }) => {
      const { combatDreadnought, orbitDreadnought } = player;
      const dreadnought = combatDreadnought.last();
      if (!dreadnought) return false;
      combatDreadnought.move(orbitDreadnought, dreadnought);
      return true;
    });
  },
  shippingTrackTroop() {
    return new AgentEffectFunction((props) => {
      this.garnison(1).apply(props);
      return this.canDeployToConflict({ amount: 3, type: "troop" }).apply(
        props
      );
    });
  },
  dreadnoughtControlSlot() {
    return new AgentEffectFunction(({ player, game }) => {
      const { combatDreadnought } = player;
      const dreadnought = combatDreadnought.last();
      if (!dreadnought) return false;
      const { actionSlots } = game;
      const controllableSlots = actionSlots.filter(
        (slot) => slot.isControllable && !slot.controlDreadnought
      );
      if (!controllableSlots.length) return false;

      return [
        {
          playerId: player.id,
          possibleChoices: controllableSlots.map((slot) => ({
            slotId: slot.id,
          })),
          type: "dreadnoughtControlSlot",
        },
      ];
    });
  },
  hasNTechTile(minAmount: number) {
    return new AgentEffectFunction(({ player }) => {
      const { techTiles } = player;
      return techTiles.pile.length >= minAmount;
    });
  },
  factionUpOnSlotWithAgent() {
    return new AgentEffectFunction(({ player, game }) => {
      const slotFactionsWithAgent = game.actionSlots.flatMap((slot) => {
        const faction = agentIconToFactionType(slot.agentIcon);
        if (
          faction === null ||
          !slot.meaples.pile.some((meaple) => meaple.owner === player.id)
        ) {
          return [];
        }
        return [faction];
      });
      const factions = deduplicate(slotFactionsWithAgent);
      factions.forEach((faction) => {
        game.factionLevelChange(faction, player, 1);
      });
      return true;
    });
  },
  opponentsRetreatDreadnought() {
    return new AgentEffectFunction(({ player, game }) => {
      const opponents = game.getOpponents(player.id);
      opponents.forEach((opponent) => {
        const { combatDreadnought, garnisonDreadnought } = opponent;
        if (combatDreadnought.length) {
          combatDreadnought.moveCount(garnisonDreadnought, 1);
        }
      });
      return true;
    });
  },
  hasDeployedNUnits(amount: number) {
    return new AgentEffectFunction(({ player }) => {
      const { startOfTurnConflict, combatDreadnought, combatTroops } = player;
      const startConflictTotal =
        startOfTurnConflict.dreadnought + startOfTurnConflict.troop;
      const nowTotal = combatDreadnought.length + combatTroops.length;
      const deployedToConflictCount = nowTotal - startConflictTotal;
      return deployedToConflictCount >= amount;
    });
  },
  grandConspiracy() {
    return new AgentEffectFunction((args) => {
      const { player } = args;
      const { orbitDreadnought, hasSiege } = player;
      const dreadnoughtCount = 2 - orbitDreadnought.length;
      const has2Dreadnought = dreadnoughtCount >= 2;

      const hasTSMFCard = player.TSMFCardCount >= 1;
      const lvl4Count = player.factionAffinities.filter(
        (affinity) => affinity.level >= 4
      ).length;
      const has2lvl4 = lvl4Count >= 2;
      const boolCount = [
        has2Dreadnought,
        hasTSMFCard,
        has2lvl4,
        hasSiege,
      ].reduce((acc, bool) => acc + (bool ? 1 : 0), 0);
      if (boolCount >= 3) {
        player.victoryPoints++;
      }
      if (boolCount >= 4) {
        player.victoryPoints++;
      }
      return true;
    });
  },
  ennemyRetreatForEachTroopDeployed() {
    return new AgentEffectFunction((args) => {
      const { game, player } = args;
      const { startOfTurnConflict, combatTroops } = player;
      const deployedTroopsCount =
        combatTroops.length - startOfTurnConflict.troop;
      if (!deployedTroopsCount) {
        return true;
      }
      const opponents = game.getOpponents(player.id);
      const deployedOpponents = opponents.filter(
        (opponent) =>
          opponent.combatTroops.length || opponent.combatDreadnought.length
      );
      const firstOpponent = deployedOpponents.at(0);
      if (!firstOpponent) {
        return true;
      } else if (deployedOpponents.length === 1) {
        game.forceRetreatNUnit(firstOpponent.id, deployedTroopsCount);
        return true;
      } else {
        return this.ennemyRetreatNUnit({
          opponentId: firstOpponent.id,
          unitCount: deployedTroopsCount,
        }).apply(args);
      }
    });
  },
  ennemyRetreatNUnit({
    opponentId,
    unitCount,
  }: {
    opponentId: string;
    unitCount: number;
  }) {
    return new AgentEffectFunction(({ player }) => {
      return [
        {
          playerId: player.id,
          type: "ennemyRetreatNUnit",
          possibleChoices: [...new Array(unitCount + 1)].map((_, index) => ({
            unitCount: index,
            opponentId,
            maxUnitCount: unitCount,
            type: "ennemyRetreatNUnit",
          })),
        },
      ];
    });
  },
  intrigueScry({
    drawCount,
    lookCount,
  }: {
    drawCount: number;
    lookCount: number;
  }) {
    return new AgentEffectFunction(({ player, game }) => {
      const { intrigueDeck } = game;
      const lookedCards = intrigueDeck.pile.slice(-lookCount);
      return [
        {
          playerId: player.id,
          type: "intrigueScry",
          possibleChoices: lookedCards.map((card) => ({
            intrigueCardId: card.id,
          })),
          choiceCount: drawCount,
        },
      ];
    });
  },
  setTurnToPlayer(playerId: string) {
    return new AgentEffectFunction(({ game }) => {
      const { players } = game;
      game.currentPlayerIndex = players.map(({ id }) => id).indexOf(playerId);
      return true;
    });
  },
  isWithEnnemyAgent() {
    return new AgentEffectFunction(({ player, slot }) => {
      if (!slot) {
        throw new Error("unexpected: should have an action slot in context");
      }
      const hasOpponentAgent = slot.meaples.pile.some(
        (meaple) => meaple.owner !== player.id
      );
      return hasOpponentAgent;
    });
  },
  dreadnoughtStrength: flagEffect,
  solarisBlessing: flagEffect,
  trashOnRevealWhenPlayingNonFaction() {
    return new AgentEffectFunction((args) => {
      const { game, player } = args;
      const { actionSlots } = game;
      const searchedIcons: AgentIcon[] = [
        AgentIcon.City,
        AgentIcon.SpiceTrade,
        AgentIcon.Landsraad,
      ];
      const matchedSlots = actionSlots.filter(
        (slot) =>
          searchedIcons.includes(slot.agentIcon) &&
          slot.meaples.pile.some((meaple) => meaple.owner === player.id)
      );
      if (matchedSlots.length >= 2) {
        return this.trashCard({ onlyInPlay: true }).apply(args);
      }
      return true;
    });
  },
  planification: flagEffect,
};

export type AgentEffectDescriptionRaw = Proposition<
  Command<typeof AgentEffectFunctionsRaw>,
  {}
>;

const AgentEffectFunctionsMeta = {
  orChoice(effects: AgentEffectDescriptionRaw[]) {
    return new AgentEffectFunction((args) => {
      const { player } = args;
      const possibleEffects = effects.filter((effect) => {
        if (AgentEffectDescription.is(effect, "acquireTechTile")) {
          const result = Command.call(AgentEffectFunctions, effect).apply(args);
          if (AgentEffectResult.isBoolean(result) && !result) {
            return false;
          } else {
            return true;
          }
        } else {
          return true;
        }
      });
      const firstPossibleEffect = possibleEffects.at(0);
      if (!firstPossibleEffect) {
        return false;
      } else if (
        possibleEffects.length === 1 &&
        AgentEffectDescription.isSingle(firstPossibleEffect)
      ) {
        return Command.call(AgentEffectFunctionsRaw, firstPossibleEffect).apply(
          args
        );
      } else {
        return [
          {
            playerId: player.id,
            possibleChoices: effects.map((effect) => ({ effect })),
            type: "orChoice",
          },
        ];
      }
    });
  },
  onWin(effect: AgentEffectDescriptionRaw) {
    return new AgentEffectFunction(({ player }) => {
      player.onWin = effect;
      return true;
    });
  },
};

export const AgentEffectFunctions = {
  ...AgentEffectFunctionsRaw,
  ...AgentEffectFunctionsMeta,
};

export const getEffectArg = <Key extends keyof typeof AgentEffectFunctions>(
  command: Command<typeof AgentEffectFunctions>
) => command.arg as Command<typeof AgentEffectFunctions, Key>["arg"];

export type AgentEffectDescription = Proposition<
  Command<typeof AgentEffectFunctions>,
  {}
>;

export const AgentEffectDescription = {
  is(
    effect: AgentEffectDescription,
    key: keyof typeof AgentEffectFunctions
  ): effect is ValueProposition<
    Command<typeof AgentEffectFunctions, typeof key>,
    {}
  > {
    return Proposition.isValueProposition(effect) && effect.methodName === key;
  },
  isSingle(
    effect: AgentEffectDescription
  ): effect is ValueProposition<
    Command<typeof AgentEffectFunctions, keyof typeof AgentEffectFunctions>,
    {}
  > {
    return Proposition.isValueProposition(effect);
  },
};

const agentEffectBuilder = Proposition.builder<
  Command<typeof AgentEffectFunctions>,
  {}
>()({});
const fct = Command.builder(AgentEffectFunctions);
const metaFct = Command.builder(AgentEffectFunctionsMeta);

const { cause: causeRaw } = Proposition.builder<
  Command<typeof AgentEffectFunctionsRaw>,
  {}
>()({});
const { cause, and } = agentEffectBuilder;

const canDeployToConflict = fct("canDeployToConflict");
const canDeployToConflictTroop = (amount: number) =>
  canDeployToConflict({ amount, type: "troop" });

export const AgentEffectCommands = {
  or: metaFct("orChoice"),
  signet: fct("signet")(undefined),
  and,
  cause,
  causeRaw,
  combatPoint: fct("combatPoint"),
  influencePoints: fct("influencePoints"),
  spice: fct("spice"),
  solaris: fct("solaris"),
  water: fct("water"),
  garnison: fct("garnison"),
  payResource: fct("payResource"),
  victoryPoint: fct("victoryPoint"),
  hasAlliance: fct("hasAlliance"),
  hasInfluenceLevel2: fct("hasInfluenceLevel2"),
  canDeployToConflict,
  canDeployToConflictTroop,
  canDeployToConflictUnit: fct("canDeployToConflictUnit"),
  canRetreat: fct("canRetreat"),
  shouldRetreat: fct("shouldRetreat"),
  opponentsDiscard: fct("opponentsDiscard"),
  opponentsLooseCombatTroop: fct("opponentsLooseCombatTroop"),
  opponentsLooseGarnisonTroop: fct("opponentsLooseGarnisonTroop"),
  chooseLooseConflictTroops: fct("chooseLooseConflictTroops"),
  chooseFactionChange: fct("chooseFactionChange"),
  influenceByPlayedFactionCard: fct("influenceByPlayedFactionCard"),
  factionUp: fct("factionUp"),
  gainControl: fct("gainControl"),
  multipleFactionChange: fct("multipleFactionChange"),
  reserveShopCardWithDiscount: fct("reserveShopCardWithDiscount"),
  TSMFDiscount: fct("TSMFDiscount"),
  hasAnyAlliance: fct("hasAnyAlliance")(undefined),
  draw: fct("draw")(undefined),
  drawIntrigue: fct("drawIntrigue")(undefined),
  trashCard: fct("trashCard")({ onlyInPlay: false }),
  trashCardInPlay: fct("trashCard")({ onlyInPlay: true }),
  trashMe: fct("trashMe")(undefined),
  trashMeOptional: fct("trashMeOptional")(undefined),
  emperorUp: fct("emperorUp")(undefined),
  guildUp: fct("guildUp")(undefined),
  beneGesseritUp: fct("beneGesseritUp")(undefined),
  fremenUp: fct("fremenUp")(undefined),
  mentat: fct("mentat")(undefined),
  takeSiege: fct("takeSiege")(undefined),
  buyWorker: fct("buyWorker")(undefined),
  buySpaceWarp: fct("buySpaceWarp")(undefined),
  stealIntrigue: fct("stealIntrigue")(undefined),
  fremenBond: fct("fremenBond")(undefined),
  hasPlayedBeneGesseritCard: fct("hasPlayedBeneGesseritCard")(undefined),
  drawBenegesseritInDiscsard: fct("drawBenegesseritInDiscsard")(undefined),
  shuffleDiscardInDeck: fct("shuffleDiscardInDeck")(undefined),
  hasSiege: fct("hasSiege")(undefined),
  canPassTurn: fct("canPassTurn")(undefined),
  recallAgent: fct("recallAgent")(undefined),
  replaceOpponentConflictTroop: fct("replaceOpponentConflictTroop")(undefined),
  ignoreAgentIcons: fct("ignoreAgentIcons")(undefined),
  ignoreOccupiedAgentSlots: fct("ignoreOccupiedAgentSlots")(undefined),
  chooseGoToBattle: fct("chooseGoToBattle"),
  endPlayerTurn: fct("endPlayerTurn")(undefined),
  endRound: fct("endRound")(undefined),
  resolveWinnerDreadnought: fct("resolveWinnerDreadnought")(undefined),
  sellSpice: fct("sellSpice")(undefined),
  takeMentatFromAnywhere: fct("takeMentatFromAnywhere")(undefined),
  doubleBaseSpice: fct("doubleBaseSpice")(undefined),
  toNextFightingPlayer: fct("toNextFightingPlayer")(undefined),
  opponentDiscardOrLooseTroop: fct("opponentDiscardOrLooseTroop")(undefined),
  chooseFactionUpWhereBehind: fct("chooseFactionUpWhereBehind")(undefined),
  playAnywhere: fct("playAnywhere")(undefined),
  drawOrTrashTopCard: fct("drawOrTrashTopCard")(undefined),
  canBuyOnDeck: fct("canBuyOnDeck")(undefined),
  buyOnDeck: fct("buyOnDeck"),
  freeCard: fct("freeCard"),
  landsraadPopularity: fct("landsraadPopularity")(undefined),
  eyesEverywhere: fct("eyesEverywhere")(undefined),
  spiceAdict: fct("spiceAdict")(undefined),
  prescience: fct("prescience")(undefined),
  siegeConnections: fct("siegeConnections")(undefined),
  ruthlessNegotiator: fct("ruthlessNegotiator")(undefined),
  masterstroke: fct("masterstroke")(undefined),
  checkEndBuy: fct("checkEndBuy")(undefined),
  checkEndReveal: fct("checkEndReveal")(undefined),
  blockActionSlot: fct("blockActionSlot")(undefined),
  discardCard: fct("discardCard")(undefined),
  freighter: fct("freighter")(undefined),
  freighterUp: fct("freighterUp")(undefined),
  recallFreighter: fct("recallFreighter")(undefined),
  acquireTechTile: fct("acquireTechTile"),
  dreadnought: fct("dreadnought")(undefined),
  discardIntrigueCard: fct("discardIntrigueCard")(undefined),
  techNegociator: fct("techNegociator")(undefined),
  combatPointPerDreadnought: fct("combatPointPerDreadnought"),
  heighlinerDiscount: fct("heighlinerDiscount"),
  hasAgentOnFactionSlot: fct("hasAgentOnFactionSlot"),
  mayPlayAgain: fct("mayPlayAgain")(undefined),
  combatPointForReavealedCard: fct("combatPointForReavealedCard"),
  hasNCardTSMF: fct("hasNCardTSMF"),
  hasMoreTSMFThanOpponents: fct("hasMoreTSMFThanOpponents")(undefined),
  hasFactionsLevel: fct("hasFactionsLevel"),
  hasMinInfluencePoints: fct("hasMinInfluencePoints"),
  spyStallites: fct("spyStallites")(undefined),
  recycleIntrigueCards: fct("recycleIntrigueCards")(undefined),
  winTiebreakers: fct("winTiebreakers")(undefined),
  hasDeployedDreadnaught: fct("hasDeployedDreadnaught")(undefined),
  shippingTrackTroop: fct("shippingTrackTroop")(undefined),
  dreadnoughtControlSlot: fct("dreadnoughtControlSlot")(undefined),
  choosePaymentForTechTile: fct("choosePaymentForTechTile"),
  opponentsSolaris: fct("opponentsSolaris"),
  hasNTechTile: fct("hasNTechTile"),
  looseTroop: fct("looseTroop"),
  hasResource: fct("hasResource"),
  hasDeployedNUnits: fct("hasDeployedNUnits"),
  factionUpOnSlotWithAgent: fct("factionUpOnSlotWithAgent")(undefined),
  opponentsRetreatDreadnought: fct("opponentsRetreatDreadnought")(undefined),
  grandConspiracy: fct("grandConspiracy")(undefined),
  isWithEnnemyAgent: fct("isWithEnnemyAgent")(undefined),
  ennemyRetreatForEachTroopDeployed: fct("ennemyRetreatForEachTroopDeployed")(
    undefined
  ),
  onWin: fct("onWin"),
  ennemyRetreatNUnit: fct("ennemyRetreatNUnit"),
  dreadnoughtStrength: fct("dreadnoughtStrength")(undefined),
  solarisBlessing: fct("solarisBlessing")(undefined),
  trashOnRevealWhenPlayingNonFaction: fct("trashOnRevealWhenPlayingNonFaction")(
    undefined
  ),
  intrigueScry: fct("intrigueScry"),
  setTurnToPlayer: fct("setTurnToPlayer"),
  planification: fct("planification")(undefined),
  drawIntrigueCardsToCheckpoint: fct("drawIntrigueCardsToCheckpoint")(
    undefined
  ),
};

export const isAgentEffectEqual = (
  a: AgentEffectDescription,
  b: AgentEffectDescription
): boolean => isJsonEqual(a, b);
